import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, MapPin } from 'lucide-react';
import { companyInfo, valueProposition, images } from '../data/mock';
import { PageSEO } from '../components/PageSEO';

const Home = () => {
  return (
    <div className="min-h-screen">
      <PageSEO path="/" />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={images.hero}
            alt="Distribución profesional"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/95 via-emerald-800/90 to-teal-900/85"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <h1 className="sm:text-6xl lg:text-7xl !font-bold !text-6xl !text-center mb-6 text-white">
              {companyInfo.tagline}
            </h1>
            <p className="sm:text-2xl !text-xl !text-center mb-8 text-emerald-100">
              Cobertura efectiva, logística eficiente y experiencia comprobada en la zona centro del Ecuador
            </p>
            <div className="flex flex-col sm:flex-row !gap-[20px]">
              <Link
                to="/contacto"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-emerald-700 font-semibold rounded-lg hover:bg-emerald-50 transition-all duration-200 shadow-lg hover:shadow-xl group">

                Contáctanos
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
              <Link
                to="/nosotros"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-emerald-700 transition-all duration-200">

                Conoce Más
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {valueProposition.map((item, index) =>
            <div
              key={index}
              className="text-center p-8 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 hover:shadow-lg transition-all duration-300 group">

                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full mb-4 group-hover:scale-110 transition-transform duration-300">
                  {item.icon === 'map' && <MapPin size={28} className="text-white" />}
                  {item.icon === 'trending-up' && <TrendingUp size={28} className="text-white" />}
                  {item.icon === 'award' && <Users size={28} className="text-white" />}
                </div>
                <h3 className="!font-bold !text-4xl mb-2 text-emerald-700">{item.stat}</h3>
                <h4 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h4>
                <p className="text-gray-600">{item.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-4">
                Quiénes Somos
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                Experiencia en Distribución
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Con base en Riobamba, somos una empresa de distribución especializada en productos de consumo masivo en la zona centro del Ecuador. Nuestro enfoque está en la cobertura regional efectiva, logística eficiente y una fuerza de ventas altamente capacitada.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Trabajamos con canales tradicionales y modernos, garantizando la presencia de nuestras marcas aliadas en cada punto de venta estratégico.
              </p>
              <Link
                to="/nosotros"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 shadow-md hover:shadow-lg group">

                Conoce Más Sobre Nosotros
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img
                  src={images.about}
                  alt="Equipo profesional"
                  className="rounded-2xl shadow-2xl w-full h-[400px] object-cover" />

                <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            ¿Listo para Crecer Juntos?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Conviértete en nuestro socio estratégico y expande tu marca en la zona centro del Ecuador
          </p>
          <Link
            to="/contacto"
            className="inline-flex items-center px-8 py-4 bg-white text-emerald-700 font-semibold rounded-lg hover:bg-emerald-50 transition-all duration-200 shadow-lg hover:shadow-xl group">

            Contáctanos Ahora
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Link>
        </div>
      </section>
    </div>);

};

export default Home;